import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { map, retry } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { PATH } from '../app.api'
import { Demand, DemandDetails, DemandDetailsSchedule, DemandNear, DetailDemandNear, ListDemandSchedule, RequestNearDemands, ZooMaps } from '../models/Demand'
import { Inventory } from '../models/Inventory'
import { NotificationService } from './notification.service'
import * as FileSaver from 'file-saver'
import { Address } from '../models/Address'
import { AllPinsFromScheduleAppraisal, ScheduleAppraisalDetails, ScheduleTreeDetails } from '../models/Appraisal'

@Injectable()
export class SchedulingService {
	constructor(
		private http: HttpClient,
		private readonly notificationService: NotificationService
	) {}

	public listAppraisalsMaps(companyId: string): Observable<Array<any>> {
		return this.http
			.get<any>(`${PATH}/appraisal/listAppraisalsMaps/${companyId}`, {
				headers: this.setHeader()
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public validateData(ids: Array<string>, mapType: string) {
		const companyId = localStorage.getItem('companyId')
		return this.http.get<any>(`${PATH}/tree/validate/scheduling/map/${companyId}?ids=${ids.join(',')}&map-type=${mapType}`, {
			headers: this.setHeader()
		})
		.pipe(
			map(result => {
				return result.content
			})
		)
	}

	public getOnlySelectedDemand(companyId: string, itensSelected: Array<string>, skip: number, limit: number, orderBy: string, typeOrder: string) {
		return this.http.get<any>(`${PATH}/demand/filter/?companyId=${companyId}&onlySelected=true&itensSelected=${itensSelected.join(',')}&skip=${skip}&limit=${limit}&order=${orderBy}&typeOrder=${typeOrder}`)
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getAllPinsSchedulingSteps(companyId: string): Observable<AllPinsFromScheduleAppraisal> {
		const options: HttpHeaders = this.setHeader()

		return this.http
			.get<any>(`${PATH}/appraisal/maps/zoom?companyId=${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getTreeEvaluatedDetails(treeId: string): Observable<ScheduleTreeDetails> {
		const options: HttpHeaders = this.setHeader()

		return this.http
			.get<any>(`${PATH}/appraisal/scheduleTreeDetails/${treeId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getAppraisalScheduleDetails(appraisalId: string): Observable<ScheduleAppraisalDetails> {
		const options: HttpHeaders = this.setHeader()

		return this.http
			.get<any>(`${PATH}/appraisal/appraisalScheduleDetails/${appraisalId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getDemandsByCompanyId(companyId: string, dateStart ?: any, dateEnd?: any): Observable<Array<any>> {
		return this.http
			.get<any>(`${PATH}/demand/all?companyId=${companyId}${dateStart ? `&dateStart=${dateStart}&dateEnd=${dateEnd}` : ''}`, {
				headers: this.setHeader()
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getExportDemandsSchedule(companyId: string, dateStart?: number, dateEnd?: number): Observable<any> {
		const options: any = this.setHeader()

		const params = new HttpParams()
						.set('companyId', companyId)
			.set('dateStart', String(dateStart))
			.set('dateEnd', String(dateEnd))

		return this.http
			.get<any>(`${PATH}/demand/schedule/all`, {
				headers: options,
				params
			})
			.pipe(
				map(result => {
					const stringBase64 = result.content

				 if (stringBase64 == undefined) {
					return this.notificationService.notify('Não há dados a serem exportados', 420)
				}
				// Decodificar a string Base64 para um array de bytes
				 const byteCharacters = atob(stringBase64)
				 const byteNumbers = new Array(byteCharacters.length)

				 for (let i = 0; i < byteCharacters.length; i++) {
				  byteNumbers[i] = byteCharacters.charCodeAt(i)
				}

				 const byteArray = new Uint8Array(byteNumbers)

				 const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

				// Realizar o download do arquivo automaticamente
				 FileSaver.saveAs(blob, `Relatório_de_agenda_de_avaliação_export_${new Date().getTime()}.xlsx`)
				})
			)
	}

	public getProblems(): Observable<Array<any>> {
		return this.http
			.get<any>(`${PATH}/problems`, { headers: this.setHeader() })
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getEvaluators(
		demand: any,
		date: string,
		companyId: string
	): Observable<any> {
		return this.http
			.post<any>(
				`${PATH}/appraisal/listEvaluatorsToAssignAvaliation`,
				{...demand, date, companyId },
				{ headers: this.setHeader() }
			)
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getDetailProblem(problem_id: string): Observable<any> {
		return this.http
			.get<any>(`${PATH}/problems/${problem_id}`, {
				headers: this.setHeader()
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getAppraisalsEvaluator(
		evaluator_id: string,
		date: string
	): Observable<any> {
		return this.http
			.post<any>(
				`${PATH}/appraisal/listAppraisalsByEvaluatorId`,
				{ evaluator_id, date },
				{ headers: this.setHeader() }
			)
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getDemandDetailsForSchedule(demandId: string): Observable<DemandDetailsSchedule> {
		return this.http.get<any>(`${PATH}/demand/details-schedule/${demandId}`, {
			headers: this.setHeader()
		}).pipe(
			  map(result => {
				return result.content
			  })
		)
	}

	public addDemand(demand: DemandDetails): Observable<any> {
		return this.http.post<any>(`${PATH}/demand`, JSON.stringify(demand), {
			headers: this.setHeader()
		})
	}

	public patchDemand(demand: DemandDetails): Observable<any> {
		return this.http.patch<any>(
			`${PATH}/demand/${demand.idMongo}`, JSON.stringify(demand), {
				headers: this.setHeader()
			})
	}

	public assignDemand(
		dataSelected: Array<Inventory | ListDemandSchedule>
	): Observable<any> {
		return this.http.post<any>(
			`${PATH}/appraisal`,
			dataSelected,
			{ headers: this.setHeader() }
		)
	}

	public removeDemand(id: string): Observable<any> {
		return this.http.delete<any>(`${PATH}/demand/${id}`, {
			headers: this.setHeader()
		})
	}

	public cancelAppraisal(id: string, demand_id: string): Observable<any> {
		return this.http.delete<any>(`${PATH}/appraisal/${id}/${demand_id}`, {
			headers: this.setHeader()
		})
	}

	public groupDemand(appraisalId: string, demandId: string): Observable<any> {
		return this.http
			.post<any>(
				`${PATH}/appraisal/group`,
				{ appraisalId, demandId },
				{ headers: this.setHeader() }
			)
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

		private setHeader(): HttpHeaders {
		return new HttpHeaders({ 'Content-Type': 'application/json' }).append( 'x-source-from', 'PAINEL')
	}

	public getDemandServiceTypesByCompanyId(companyId: string): Observable<Array<any>> {
		return this.http
			.get<any>(`${PATH}/demand/service-type/company/${companyId}`, { headers: this.setHeader() })
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getDemandDetailsForId(demandId: string): Observable<DemandDetails> {
		return this.http
			.get<any>(`${PATH}/demand/details/${demandId}`, {headers: this.setHeader() })
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public createDemandServiceType(name: string, companyId: string): Observable<any> {
		return this.http
			.post<any>(
				`${PATH}/demand/service-type`,
				{
					name,
					companyId
				},
				{
					headers: this.setHeader()
				}
			)
			.pipe(
				map(result => {
					return result
				})
			)
	}

	public removeDemandServiceType(serviceTypeId: number): Observable<any> {
		return this.http
			.delete<any>(
				`${PATH}/demand/service-type/${serviceTypeId}`,
				{
					headers: this.setHeader()
				}
			).pipe(
				map(result => {
					return result
				})
			)
	}

	public editDemandServiceType(id: number, name: string): Observable<any> {
		return this.http
			.patch<any>(
				`${PATH}/demand/service-type/${id}/${name}`,
				{
					headers: this.setHeader()
				}
			).pipe(
				map(result => {
					return result
				})
			)
	}

	public getDemandRegionsByCompanyId(companyId: string): Observable<Array<any>> {
		return this.http
			.get<any>(`${PATH}/demand/region/company/${companyId}`, { headers: this.setHeader() })
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getAppraisalScheduleMap(companyId: string): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/demand/schedule/map/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getQuestionStatScheduleAppraisal(companyId: string): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/demand/schedule/questions/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public updateMapCacheSchedule(companyId: string, userEmail: string): Observable<Array<any>> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/demand/schedule/${companyId}/${userEmail}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getClickScheduleDemand(plantingId: string, companyId: string): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/demand/schedule/getClick/${plantingId}/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public updateLocationPinMapSchedule(demandId: string, location: Address): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/demand/maps/schedule/updatePin/${demandId}`, JSON.stringify(location), {
				headers: options
			})
			.pipe(
				map(result => {
					return result
				})
			)
	}

	public getNearDemands(body: RequestNearDemands): Observable<Array<DemandNear>> {
		const options: any = this.setHeader()

		return this.http
			.post<any>(`${PATH}/demand/near`, body, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getDetailsNearDemand(demandId: string): Observable<DetailDemandNear> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/demand/details-near-demand/${demandId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}
}
